import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { AdminShell } from '../../components/AdminShell';
import { loadAllShops, mrrFor, healthFor, colourForShop } from '../../lib/adminQueries';
import { Plus, Search } from 'lucide-react';

const fmtMoney = (n) => `£${Number(n || 0).toFixed(0)}`;
const initials = (name) => (name || '').split(/\s+/).filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?';

const BAND_PILL = {
  green: { cls: 'green', label: 'Green' },
  amber: { cls: 'amber', label: 'Amber' },
  red:   { cls: 'red',   label: 'Red' },
};
const PLAN_PILL = (plan) => plan === 'premium'
  ? { cls: 'prem',  label: 'Premium' }
  : { cls: 'gray',  label: 'Standard' };
const STATUS_PILL = (s) => {
  if (!s || s === 'cancelled') return { cls: 'gray', label: 'Not subscribed' };
  if (s === 'active' || s === 'trialing') return { cls: 'green', label: 'Active' };
  if (s === 'past_due' || s === 'unpaid') return { cls: 'amber', label: 'Past due' };
  return { cls: 'gray', label: s };
};

const ShopsInner = () => {
  const [shops, setShops] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [q, setQ] = useState('');
  const [bookingCounts, setBookingCounts] = useState({});      // {shop_id: {this:#, last:#}}

  const load = async () => {
    setLoading(true);
    setLoadError('');
    let all = [];
    try {
      all = await loadAllShops();
    } catch (e) {
      setLoadError(e.message || 'Failed to load shops');
      setShops([]);
      setLoading(false);
      return;
    }
    const { data: s } = await supabase.from('platform_settings').select('*').limit(1).maybeSingle();
    setSettings(s);

    // 30-day bookings counts and last-7d vs prev-7d for health
    const ids = all.map(x => x.id);
    if (ids.length) {
      const now = new Date();
      const day = 86400000;
      const thirtyAgo = new Date(now.getTime() - 30 * day).toISOString();
      const sevenAgo  = new Date(now.getTime() - 7  * day).toISOString();
      const fourteenAgo = new Date(now.getTime() - 14 * day).toISOString();

      const { data: bks } = await supabase
        .from('bookings').select('shop_id,starts_at,status')
        .in('shop_id', ids)
        .gte('starts_at', thirtyAgo);
      const counts = {};
      ids.forEach(id => { counts[id] = { total30: 0, thisWeek: 0, lastWeek: 0 }; });
      (bks || []).forEach(b => {
        if (b.status === 'cancelled') return;
        const t = new Date(b.starts_at).getTime();
        const c = counts[b.shop_id];
        c.total30 += 1;
        if (t >= new Date(sevenAgo).getTime()) c.thisWeek += 1;
        else if (t >= new Date(fourteenAgo).getTime()) c.lastWeek += 1;
      });
      setBookingCounts(counts);
    }
    setShops(all);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const enriched = useMemo(() => shops.map(sh => {
    const bc = bookingCounts[sh.id] || { total30: 0, thisWeek: 0, lastWeek: 0 };
    const mrr = mrrFor(sh, settings);
    const health = healthFor({
      subscriptionStatus: sh.subscription_status,
      lastLoginAt: null, // not joined here; churn screen uses auth.users
      bookingsThisWeek: bc.thisWeek,
      bookingsLastWeek: bc.lastWeek,
    });
    return { ...sh, mrr, bookings30: bc.total30, band: health.band };
  }), [shops, bookingCounts, settings]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return enriched;
    return enriched.filter(sh =>
      sh.name?.toLowerCase().includes(s) ||
      sh.subdomain?.toLowerCase().includes(s)
    );
  }, [q, enriched]);

  return (
    <main className="main admin-page" data-testid="admin-shops-page">
      <div className="topbar">
        <div>
          <h2>Shops</h2>
          <div className="sub">{shops.length} shop{shops.length === 1 ? '' : 's'} on Instacut</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <div className="search">
            <Search size={14} />
            <input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-shops-search" />
          </div>
          <Link to="/admin/onboard" className="btn p" data-testid="admin-add-shop">
            <Plus size={14} /> Onboard shop
          </Link>
        </div>
      </div>

      <div className="body">
        {loadError && (
          <div className="modal-error" data-testid="admin-shops-error" style={{ marginBottom: 12 }}>
            Couldn&apos;t load shops: {loadError}
          </div>
        )}
        <div
          className="panel"
          data-testid="admin-shops-table"
          style={{ width: 'max-content', maxWidth: '100%' }}
        >
          {loading ? (
            <div style={{ padding: 24 }}>
              {[1,2,3].map(i => <div key={i} className="skel-line" style={{ width: `${60 + i*8}%` }} />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="statebox" style={{ border: 'none', padding: '40px 24px' }}>
              <h5>No shops yet</h5>
              <p>When new shops sign up, they&apos;ll appear here.</p>
            </div>
          ) : (
            <table className="table" style={{ width: 'auto' }}>
              <thead>
                <tr>
                  <th>Shop</th>
                  <th>Plan</th>
                  <th>Billing</th>
                  <th>MRR</th>
                  <th>30-day bookings</th>
                  <th>Health</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(sh => {
                  const planPill = PLAN_PILL(sh.plan);
                  const statusPill = STATUS_PILL(sh.subscription_status);
                  const bandPill = BAND_PILL[sh.band];
                  return (
                    <tr
                      key={sh.id}
                      className="clickable"
                      onClick={() => window.location.assign(`/admin/shops/${sh.id}`)}
                      data-testid={`admin-shop-row-${sh.id}`}
                    >
                      <td data-th="Shop">
                        <div className="shop">
                          <span className="sq" style={{ background: colourForShop(sh) }}>
                            {initials(sh.name).charAt(0)}
                          </span>
                          <div>
                            <b>{sh.name}</b>
                            <small>{sh.subdomain}.instacut.co.uk</small>
                          </div>
                        </div>
                      </td>
                      <td data-th="Plan"><span className={`pill ${planPill.cls}`}>{planPill.label}</span></td>
                      <td data-th="Billing"><span className={`pill ${statusPill.cls}`}>{statusPill.label}</span></td>
                      <td className="mono" data-th="MRR">{fmtMoney(sh.mrr)}</td>
                      <td className="mono" data-th="30-day bookings">{sh.bookings30}</td>
                      <td data-th="Health">
                        <span className={`pill ${bandPill.cls}`} style={bandPill.style || {}}>{bandPill.label}</span>
                      </td>
                      <td className="tac" data-th="">
                        <Link to={`/admin/shops/${sh.id}`} className="link" onClick={(e) => e.stopPropagation()}>Open →</Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </main>
  );
};

const Shops = () => <AdminShell><ShopsInner /></AdminShell>;
export default Shops;
